/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS, NAV_PAGE_LIST } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getUsersListSelector,
	getIsUsersListLoadingSelector,
	getPaginationSelector,
	getUsersRolesListSelector,
} from './selectors';
import { getUsersListAction, getUsersRolesListAction, resetStateAction } from './actions';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { getIsAvailableUsersButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const UsersContainerWrapper = ({
	onGetUsersList,
	usersList,
	isUsersListLoading,
	pagination,
	onResetState,
	isAvailable,
	onGetUsersRolesList,
	usersRolesList,
}) => {
	useInjectSaga({ key: 'usersContainerSaga', saga });
	useInjectReducer({ key: 'usersContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetUsersList({ searchQuery: removeModalQuery(query.toString()) });
		onGetUsersRolesList();

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'user');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetUsersList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetUsersList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetUsersList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS(usersRolesList)));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetUsersList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={NAV_PAGE_LIST}
			pageContent={
				<DataGridComponent
					headerText="Пользователи"
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					columns={TABLE_COLUMNS(usersRolesList)}
					rows={usersList}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isUsersListLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	usersList: getUsersListSelector(),
	isUsersListLoading: getIsUsersListLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableUsersButtonsSelector(),
	usersRolesList: getUsersRolesListSelector(),
});

const mapDispatchToProps = {
	onGetUsersList: getUsersListAction,
	onGetUsersRolesList: getUsersRolesListAction,
	onResetState: resetStateAction,
};

const UsersContainer = connect(mapStateToProps, mapDispatchToProps)(UsersContainerWrapper);

UsersContainerWrapper.propTypes = {
	onGetUsersList: PropTypes.func.isRequired,
	onGetUsersRolesList: PropTypes.func.isRequired,
	usersList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
			role: PropTypes.object.isRequired,
			post: PropTypes.string.isRequired,
			phone: PropTypes.string.isRequired,
			email: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
	),
	isUsersListLoading: PropTypes.bool.isRequired,
	usersRolesList: PropTypes.array,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default UsersContainer;
